import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { LANGS, type Lang } from '../pages/landing/copy';

/*
 * Language of the marketing landing: English or German.
 *
 * A browser does not tell a web page the visitor's keyboard layout, so the default is
 * the first of the browser's preferred languages that the landing speaks, English
 * otherwise. The header switch stores a manual choice that survives reloads. The
 * resolved language goes on <html lang> for screen readers and hyphenation.
 *
 * A switch fades the page text out, swaps the words while nothing is legible, and
 * fades back in (`lang-out` in landing.css). The header stays put, so the menu the
 * visitor just used never flickers. Reduced motion swaps at once.
 */

const STORAGE_KEY = 'chefty-lang';
/** Keep in sync with the `lang-out` fade in landing.css. */
const FADE_MS = 160;
const FADE_CLASS = 'lang-out';

const isLang = (value: unknown): value is Lang => LANGS.includes(value as Lang);

function storedLang(): Lang | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return isLang(value) ? value : null;
  } catch {
    return null;
  }
}

function browserLang(): Lang {
  const preferred = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const tag of preferred) {
    const base = tag?.toLowerCase().split('-')[0];
    if (isLang(base)) return base;
  }
  return 'en';
}

export function useLandingLang() {
  const [lang, setLang] = useState<Lang>(() => storedLang() ?? browserLang());

  useLayoutEffect(() => {
    const root = document.documentElement;
    const previous = root.lang;
    root.lang = lang;
    return () => {
      root.lang = previous;
    };
  }, [lang]);

  const timer = useRef(0);
  useEffect(
    () => () => {
      window.clearTimeout(timer.current);
      document.documentElement.classList.remove(FADE_CLASS);
    },
    [],
  );

  const choose = useCallback((next: Lang) => {
    try {
      // Picking what the browser would pick anyway is the same as having no override.
      if (next === browserLang()) localStorage.removeItem(STORAGE_KEY);
      else localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* storage blocked: the choice lasts for this visit only */
    }
    const root = document.documentElement;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setLang(next);
      return;
    }
    window.clearTimeout(timer.current);
    root.classList.add(FADE_CLASS);
    timer.current = window.setTimeout(() => {
      setLang(next);
      requestAnimationFrame(() => root.classList.remove(FADE_CLASS));
    }, FADE_MS);
  }, []);

  return { lang, choose };
}
